import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const itemRowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];
    const groupControlsRow = cartItemRowToRemove.dataset.key
      ? Array.from(this.querySelectorAll('[data-ace-group-controls-key]')).find(
          (row) => row instanceof HTMLElement && row.dataset.aceGroupControlsKey === cartItemRowToRemove.dataset.key
        )
      : null;
    const rowsToRemove = [...itemRowsToRemove, groupControlsRow].filter(Boolean);

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = itemRowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Removes one ACE personalization child line and cleans the linked parent line properties.
   * @param {string} lineKey - The child line item key.
   * @param {Event} event - The click event.
   */
  onAcePersonalizationChildRemove(lineKey, event) {
    event.preventDefault();
    if (!lineKey) return;

    const childRow = this.refs.cartItemRows.find((row) => row.dataset.key === lineKey);
    if (childRow) {
      const remove = () => childRow.remove();

      if (prefersReducedMotion()) {
        remove();
      } else {
        childRow.style.setProperty('--row-height', `${childRow.clientHeight}px`);
        childRow.classList.add('removing');
        onAnimationEnd(childRow, remove);
      }
    }

    this.#removeAcePersonalizationChild(lineKey);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    const lineItemRow = this.refs.cartItemRows[line - 1];
    const parentLineKey = lineItemRow?.dataset.key || '';

    const sectionsToUpdate = this.#sectionsToUpdate();

    cartTotal?.shimmer();

    this.#performQuantityUpdate({
      line,
      quantity,
      parentLineKey,
      sections: sectionsToUpdate,
    })
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Performs a quantity update.
   * Nested cart line rows must keep child quantities aligned with the visible parent row.
   * @param {{ line: number, quantity: number, parentLineKey: string, sections: string[] }} config
   * @returns {Promise<Response>}
   */
  async #performQuantityUpdate(config) {
    const { line, quantity, parentLineKey, sections } = config;
    const sectionsUrl = window.location.pathname;
    const defaultRequest = () =>
      fetch(
        Theme.routes.cart_change_url,
        fetchConfig('json', {
          body: JSON.stringify({
            line,
            quantity,
            sections: sections.join(','),
            sections_url: sectionsUrl,
          }),
        })
      );

    if (!parentLineKey) {
      return defaultRequest();
    }

    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: {
        Accept: 'application/json',
      },
    });
    const cart = await response.json();
    const parentItem = (cart.items || []).find((item) => item.key === parentLineKey);
    // One-time fee lines (`_ace_fee`, e.g. Stickdatei-Erstellung) keep quantity 1
    // and are excluded from the parent→child quantity sync.
    const childItems = (cart.items || []).filter(
      (item) =>
        item.parent_relationship?.parent_key === parentLineKey &&
        this.#isAcePersonalizationChild(item) &&
        !item.properties?._ace_fee
    );

    if (!parentItem || childItems.length === 0 || quantity === 0) {
      return defaultRequest();
    }

    const matchingItems = [...childItems, parentItem];

    for (let index = 0; index < matchingItems.length; index += 1) {
      const item = matchingItems[index];
      const isLastItem = index === matchingItems.length - 1;
      const body = JSON.stringify({
        id: item.key,
        quantity,
        ...(isLastItem
          ? {
              sections: sections.join(','),
              sections_url: sectionsUrl,
            }
          : {}),
      });

      const updateResponse = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));

      if (!isLastItem) {
        const parsedResponse = await updateResponse.json();
        if (parsedResponse.errors) {
          throw new Error(parsedResponse.errors);
        }
        continue;
      }

      return updateResponse;
    }

    return defaultRequest();
  }

  /**
   * Removes an ACE personalization child and updates the parent line's properties.
   * @param {string} childLineKey
   */
  async #removeAcePersonalizationChild(childLineKey) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('clear:user-action');
    this.#disableCartItems();
    this.refs.cartTotal?.shimmer();

    const sections = this.#sectionsToUpdate();
    const sectionsUrl = window.location.pathname;

    try {
      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: {
          Accept: 'application/json',
        },
      });
      const cart = await cartResponse.json();
      const childItem = (cart.items || []).find((item) => item.key === childLineKey);

      if (!childItem || !this.#isAcePersonalizationChild(childItem)) {
        await this.#refreshCartSections(sections);
        return;
      }

      const parentLineKey = this.#getParentLineKey(childItem);
      const parentItem = (cart.items || []).find((item) => item.key === parentLineKey);

      const removeChildResponse = await fetch(
        Theme.routes.cart_change_url,
        fetchConfig('json', {
          body: JSON.stringify({
            id: childLineKey,
            quantity: 0,
          }),
        })
      );
      const removedCart = await removeChildResponse.json();
      if (removedCart.errors) throw new Error(removedCart.errors);

      if (!parentItem) {
        await this.#refreshCartSections(sections);
        return;
      }

      const remainingChildren = (cart.items || []).filter((item) => {
        return item.key !== childLineKey && this.#getParentLineKey(item) === parentLineKey && this.#isAcePersonalizationChild(item);
      });

      // When the last real position line is removed, one-time fee lines
      // (Stickdatei-Erstellung) have nothing left to bill — remove them too.
      const remainingRealChildren = remainingChildren.filter((item) => !item.properties?._ace_fee);
      if (remainingRealChildren.length === 0) {
        for (const feeItem of remainingChildren.filter((item) => item.properties?._ace_fee)) {
          const feeRemoveResponse = await fetch(
            Theme.routes.cart_change_url,
            fetchConfig('json', {
              body: JSON.stringify({
                id: feeItem.key,
                quantity: 0,
              }),
            })
          );
          const feeRemovedCart = await feeRemoveResponse.json();
          if (feeRemovedCart.errors) throw new Error(feeRemovedCart.errors);
        }
      }

      const nextParentProperties = this.#buildAceParentProperties(
        parentItem.properties || {},
        childItem,
        remainingRealChildren
      );

      const parentUpdateResponse = await fetch(
        Theme.routes.cart_change_url,
        fetchConfig('json', {
          body: JSON.stringify({
            id: parentLineKey,
            properties: nextParentProperties,
            sections: sections.join(','),
            sections_url: sectionsUrl,
          }),
        })
      );

      const parsedResponseText = await parentUpdateResponse.json();
      resetShimmer(this);

      if (parsedResponseText.errors) {
        throw new Error(parsedResponseText.errors);
      }

      const newSectionHTML = new DOMParser().parseFromString(
        parsedResponseText.sections[this.sectionId],
        'text/html'
      );
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.#updateQuantitySelectors(parsedResponseText);
      this.dispatchEvent(
        new CartUpdateEvent(parsedResponseText, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections,
        })
      );
      morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');
      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      console.error(error);
      await this.#refreshCartSections(sections);
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * @returns {string[]}
   */
  #sectionsToUpdate() {
    const sectionsToUpdate = new Set([this.sectionId]);
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    return Array.from(sectionsToUpdate);
  }

  /**
   * @param {string[]} sections
   */
  async #refreshCartSections(sections) {
    const response = await fetch(
      `${Theme.routes.cart_url}?sections=${encodeURIComponent(sections.join(','))}&sections_url=${encodeURIComponent(window.location.pathname)}`,
      {
        headers: {
          Accept: 'application/json',
        },
      }
    );
    const data = await response.json();
    if (data?.[this.sectionId]) {
      morphSection(this.sectionId, data[this.sectionId], this.isDrawer ? 'hydration' : 'full');
    }
  }

  /**
   * @param {{ properties?: Record<string, string> }} item
   * @returns {boolean}
   */
  #isAcePersonalizationChild(item) {
    const properties = item.properties || {};
    return properties._ace_bundle === 'true' && properties._ace_bundle_role === 'child';
  }

  /**
   * @param {{ parent_relationship?: { parent_key?: string, parent?: { key?: string } } }} item
   * @returns {string}
   */
  #getParentLineKey(item) {
    return item.parent_relationship?.parent_key || item.parent_relationship?.parent?.key || '';
  }

  /**
   * @param {Record<string, string>} parentProperties
   * @param {{ properties?: Record<string, string> }} removedChild
   * @param {Array<{ properties?: Record<string, string> }>} remainingChildren
   * @returns {Record<string, string>}
   */
  #buildAceParentProperties(parentProperties, removedChild, remainingChildren) {
    const nextProperties = { ...parentProperties };
    const removedProperties = removedChild.properties || {};
    const removedUploadKey = removedProperties._ace_upload_property_key || '';
    const remainingUploadKeys = new Set(
      remainingChildren.map((item) => item.properties?._ace_upload_property_key).filter(Boolean)
    );
    const remainingCodes = remainingChildren
      .map((item) => item.properties?._ace_placement_code)
      .filter(Boolean);

    const acePropertyKeys = new Set([
      '_ace_bundle',
      '_ace_bundle_role',
      '_ace_bundle_group_id',
      '_ace_bundle_method',
      '_ace_parent_variant_id',
      '_ace_selected_codes',
      'Personalization group id',
      'Personalization bundle',
      'Bundle role',
      'Bundle parent variant id',
      'Bundle parent title',
      'Selected placements',
      'Print method',
      'Logo file',
      'Position',
      'Größe',
      'Druckart',
      'Logo-Datei',
    ]);

    if (remainingChildren.length === 0) {
      for (const key of Object.keys(nextProperties)) {
        if (acePropertyKeys.has(key) || this.#isAceUploadProperty(key)) {
          delete nextProperties[key];
        }
      }

      return nextProperties;
    }

    if (removedUploadKey) {
      delete nextProperties[removedUploadKey];
    }

    for (const key of Object.keys(nextProperties)) {
      if (this.#isAceUploadProperty(key) && !remainingUploadKeys.has(key)) {
        delete nextProperties[key];
      }
    }

    nextProperties._ace_selected_codes = remainingCodes.join(',');
    return nextProperties;
  }

  /**
   * @param {string} key
   * @returns {boolean}
   */
  #isAceUploadProperty(key) {
    return key.toLowerCase().includes('logo file');
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      const input = selector.querySelector('input[data-cart-line][data-cart-quantity]');
      const line = Number(input?.getAttribute('data-cart-line') || 0);
      const item = line > 0 ? updatedCart.items[line - 1] : null;

      if (!input || !item) continue;

      input.setAttribute('data-cart-quantity', item.quantity.toString());

      if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
        selector.updateCartQuantity();
      }
    }

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]:not(cart-quantity-selector-component)`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
